import { useReducer, useState } from "react";

import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import { useGenreRepository } from "@/pages/genres/hooks/use-genre-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

import { INTERACTION_TYPES_FOR } from "../constants";
import { DecayPeriod } from "../types/decay";
import { SandboxEntity, SandboxEntityType } from "../types/entities";
import {
  INTERACTION_BUILDER_ACTION_TYPES,
  InteractionBuilderAction,
  InteractionBuilderState,
} from "../types/interactionBuilderState";
import { Interaction, InteractionType } from "../types/interactions";

const useInteractionBuilder = () => {
  const { get: getArtist } = useArtistRepository();
  const { get: getVenue } = useVenueRepository();
  const { get: getGenre } = useGenreRepository();
  const { get: getEvent } = useEventRepository();
  const [entityDetailsCache, setEntityDetailsCache] = useState<{
    [key: string]: string;
  }>({});

  const interactionsReducer = (
    state: InteractionBuilderState,
    action: InteractionBuilderAction,
  ): InteractionBuilderState => {
    switch (action.type) {
      case INTERACTION_BUILDER_ACTION_TYPES.SET_ENTITY_TYPE:
        const allowedTypes = INTERACTION_TYPES_FOR[action.payload];
        return {
          ...state,
          _entityType: action.payload,
          _interactionType: allowedTypes.includes(state._interactionType)
            ? state._interactionType
            : allowedTypes[0],
          _selectedEntity: undefined,
        };
      case INTERACTION_BUILDER_ACTION_TYPES.SET_SELECTED_ENTITY:
        return {
          ...state,
          _selectedEntity: action.payload,
        };
      case INTERACTION_BUILDER_ACTION_TYPES.SET_INTERACTION_TYPE:
        return {
          ...state,
          _interactionType: action.payload,
        };
      case INTERACTION_BUILDER_ACTION_TYPES.SET_SELECTED_PERIOD:
        return {
          ...state,
          _selectedPeriod: action.payload,
        };
      case INTERACTION_BUILDER_ACTION_TYPES.ADD_INTERACTION:
        if (!state._selectedEntity || !state._selectedPeriod) return state;
        const newInteraction: Interaction = {
          id: action.payload.id,
          entityType: state._entityType,
          interactionType: state._interactionType,
          entity: {
            ...state._selectedEntity,
            embedding: undefined,
            isLoadingEmbedding: false,
          },
          decayPeriod: state._selectedPeriod,
        };
        return {
          ...state,
          interactions: [...state.interactions, newInteraction],
          _selectedEntity: undefined,
        };
      case INTERACTION_BUILDER_ACTION_TYPES.REMOVE_INTERACTION:
        return {
          ...state,
          interactions: state.interactions.filter(
            (interaction) => interaction.id !== action.payload,
          ),
        };
      case INTERACTION_BUILDER_ACTION_TYPES.RESET_INTERACTIONS:
        return { ...state, interactions: action.payload };
      case INTERACTION_BUILDER_ACTION_TYPES.SET_ENTITY_EMBEDDING:
        return {
          ...state,
          interactions: state.interactions.map((interaction) => {
            if (interaction.id === action.payload.interactionId) {
              return {
                ...interaction,
                entity: {
                  ...interaction.entity,
                  ...(action.payload.name
                    ? { name: action.payload.name }
                    : {}),
                  details: action.payload.details ?? interaction.entity.details ?? "",
                  embedding: action.payload.embedding || undefined,
                  isLoadingEmbedding: false,
                  embeddingError: action.payload.error,
                },
              };
            }
            return interaction;
          }),
        };
      default:
        return state;
    }
  };

  const initialState: InteractionBuilderState = {
    _entityType: "artist",
    _selectedEntity: undefined,
    _interactionType: "view",
    _selectedPeriod: "q1",
    interactions: [],
  };

  const [state, dispatch] = useReducer(interactionsReducer, initialState);

  const setEntityType = (entityType: SandboxEntityType) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.SET_ENTITY_TYPE,
      payload: entityType,
    });
  };

  const setSelectedEntity = (entity: SandboxEntity | undefined) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.SET_SELECTED_ENTITY,
      payload: entity,
    });
  };

  const setInteractionType = (interactionType: InteractionType) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.SET_INTERACTION_TYPE,
      payload: interactionType,
    });
  };

  const setSelectedPeriod = (period: DecayPeriod) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.SET_SELECTED_PERIOD,
      payload: period,
    });
  };

  const formatArtistDetails = (artist: {
    name: string;
    about?: string | null;
    genres?: { name: string }[] | null;
    relatedArtists?: { name: string }[] | null;
    stats?: {
      spotify?: { topCities?: { name: string; country: string }[] };
    } | null;
  }) => {
    const details: string[] = [];
    if (artist.name) details.push(artist.name);
    if (artist.about) details.push(`About: ${artist.about}`);
    if (artist.genres && artist.genres.length > 0) {
      details.push(`Genres: ${artist.genres.map((g) => g.name).join(", ")}`);
    }
    if (artist.relatedArtists && artist.relatedArtists.length > 0) {
      details.push(
        `Related Artists: ${artist.relatedArtists
          .map((r) => r.name)
          .join(", ")}`,
      );
    }
    if (
      artist.stats?.spotify?.topCities &&
      artist.stats.spotify.topCities.length > 0
    ) {
      details.push(
        `Top Cities: ${artist.stats.spotify.topCities
          .map((c) => `${c.name} (${c.country})`)
          .join(", ")}`,
      );
    }
    return details.length > 0 ? ` [${details.join(" · ")}]` : "";
  };

  const formatGenreDetails = (genre: {
    name: string;
    about?: string;
    parents?: string[];
  }) => {
    const details: string[] = [];
    if (genre.name) details.push(genre.name);
    if (genre.about) details.push(`About: ${genre.about}`);
    if (genre.parents && genre.parents.length > 0) {
      details.push(`Parents: ${genre.parents.join(", ")}`);
    }
    return details.length > 0 ? ` [${details.join(" · ")}]` : "";
  };

  const formatVenueDetails = (venue: {
    name: string;
    about?: string | null;
    type?: string | null;
    geodata?: { country?: string; name?: string };
  }) => {
    const details: string[] = [];
    if (venue.name) details.push(venue.name);
    if (venue.type) details.push(`Type: ${venue.type}`);
    if (venue.about) details.push(`About: ${venue.about}`);
    if (venue.geodata) {
      const location = [venue.geodata.name, venue.geodata.country]
        .filter(Boolean)
        .join(", ");
      if (location) details.push(location);
    }
    return details.length > 0 ? ` [${details.join(" · ")}]` : "";
  };

  const formatEventDetails = (e: {
    name: string;
    about?: string | null;
    genres?: { name: string }[] | null;
    lineup?: { name: string }[] | null;
    venue?: { name: string } | null;
  }) => {
    const details: string[] = [];
    if (e.name) details.push(e.name);
    if (e.about) details.push(`About: ${e.about}`);
    if (e.genres && e.genres.length > 0) {
      details.push(`Genres: ${e.genres.map((g) => g.name).join(", ")}`);
    }
    if (e.lineup && e.lineup.length > 0) {
      details.push(`Lineup: ${e.lineup.map((a) => a.name).join(", ")}`);
    }
    if (e.venue?.name) details.push(`Venue: ${e.venue.name}`);
    return details.length > 0 ? ` [${details.join(" · ")}]` : "";
  };

  const fetchEntityData = async (
    entityType: SandboxEntityType,
    entityId: string,
  ): Promise<string> => {
    switch (entityType) {
      case "artist":
        let artistDetails: string;
        if (entityDetailsCache[`artist-${entityId}`]) {
          artistDetails = entityDetailsCache[`artist-${entityId}`];
        } else {
          const artist = await getArtist(entityId);
          artistDetails = formatArtistDetails(artist);
          setEntityDetailsCache((prev) => ({
            ...prev,
            [`artist-${entityId}`]: artistDetails,
          }));
        }
        return artistDetails;
      case "venue":
        let venueDetails: string;
        if (entityDetailsCache[`venue-${entityId}`]) {
          venueDetails = entityDetailsCache[`venue-${entityId}`];
        } else {
          const venue = await getVenue(entityId);
          venueDetails = formatVenueDetails(venue);
          setEntityDetailsCache((prev) => ({
            ...prev,
            [`venue-${entityId}`]: venueDetails,
          }));
        }
        return venueDetails;
      case "genre":
        let genreDetails: string;
        if (entityDetailsCache[`genre-${entityId}`]) {
          genreDetails = entityDetailsCache[`genre-${entityId}`];
        } else {
          const genre = await getGenre(entityId);
          genreDetails = formatGenreDetails(genre);
          setEntityDetailsCache((prev) => ({
            ...prev,
            [`genre-${entityId}`]: genreDetails,
          }));
        }
        return genreDetails;
      case "event":
        let eventDetails: string;
        if (entityDetailsCache[`event-${entityId}`]) {
          eventDetails = entityDetailsCache[`event-${entityId}`];
        } else {
          const event = await getEvent(entityId);
          eventDetails = formatEventDetails(event);
          setEntityDetailsCache((prev) => ({
            ...prev,
            [`event-${entityId}`]: eventDetails,
          }));
        }
        return eventDetails;
      default:
        return "";
    }
  };

  const addInteraction = () => {
    const entity = state._selectedEntity;
    const entityType = state._entityType;
    if (!entity || !state._selectedPeriod) return;

    const id = crypto.randomUUID();
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.ADD_INTERACTION,
      payload: { id },
    });

    const entityId = entity.id;
    if (!entityId) return;

    fetchEntityData(entityType, entityId)
      .then((details) => {
        dispatch({
          type: INTERACTION_BUILDER_ACTION_TYPES.SET_ENTITY_EMBEDDING,
          payload: { interactionId: id, embedding: null, details },
        });
      })
      .catch(() => {});
  };

  const removeInteraction = (id: string) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.REMOVE_INTERACTION,
      payload: id,
    });
  };

  const resetInteractions = (interactions: Interaction[]) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.RESET_INTERACTIONS,
      payload: interactions,
    });
  };

  const updateInteractionEmbedding = (
    interactionId: string,
    embedding: number[] | null,
    error?: string,
    details?: string,
    name?: string,
  ) => {
    dispatch({
      type: INTERACTION_BUILDER_ACTION_TYPES.SET_ENTITY_EMBEDDING,
      payload: { interactionId, embedding, error, details, name },
    });
  };

  return {
    state,
    setEntityType,
    setSelectedEntity,
    setInteractionType,
    setSelectedPeriod,
    addInteraction,
    removeInteraction,
    resetInteractions,
    updateInteractionEmbedding,
  };
};

export default useInteractionBuilder;
